// =====================================================
// PROFESSIONAL TRADING JOURNAL - MAIN COMPONENT
// =====================================================
// Shows the journal list view

import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';
import JournalList from './JournalList';
import { useDesignVersion } from '@/hooks/useDesignVersion';

const { FiBook, FiX } = FiIcons;

interface ProfessionalJournalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProfessionalJournal: React.FC<ProfessionalJournalProps> = ({ isOpen, onClose }) => {
  const { version: designVersion } = useDesignVersion();
  const isV3 = designVersion === 'v3';
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-6xl max-h-[90vh] bg-gradient-to-b from-gray-900 to-gray-950 rounded-2xl border border-purple-500/30 overflow-hidden shadow-2xl shadow-purple-500/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — V3 shows the institutional proof eyebrow + new title style;
            V1 shows the pre-viral plain title (Trading Journal). */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-gray-900/80">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/15 to-purple-500/15 border border-cyan-400/30 flex items-center justify-center">
              <SafeIcon icon={FiBook} className="w-5 h-5 text-cyan-300" />
            </div>
            <div>
              {isV3 && (
                <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">▸ institutional proof system</span>
              )}
              <h2 className="text-lg font-bold text-white">Trading Journal</h2>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
          <JournalList />
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProfessionalJournal;
